import { formatTime } from "~/common/utils"
import { Text } from "~/components/Text"
import {
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type SyntheticEvent,
} from "react"
import { PauseCircleFilled, PlayCircleFilled } from "@ant-design/icons"
import { Button, Flex, Slider } from "antd"
import classNames from "classnames"
import {
  defaultPreviewFrameSize,
  getPreviewFrameSizeClassName,
  previewFrameClassName,
  type PreviewFrameSize,
} from "~/components/ResourcePreview/previewFrame"
import styles from "./index.css"

type AudioPreviewProps = {
  src: string
  fingerprint?: string
  label?: string
  className?: string
  size?: PreviewFrameSize
}

const previewPalettes = [
  ["#EAF3FF", "#B9D7FF", "#2F6BFF"],
  ["#F2F8EF", "#BFE6B7", "#3D8B40"],
  ["#FFF4E6", "#FFD39B", "#D46B08"],
  ["#F4EEFF", "#D8C2FF", "#722ED1"],
  ["#EAF8F7", "#A6E3DD", "#08979C"],
  ["#FFF0F6", "#FFADD2", "#C41D7F"],
]

const waveBarCount = 18

const stopPropagation = (event: SyntheticEvent) => {
  event.stopPropagation()
}

const getFiniteMediaTime = (value: number) => {
  return Number.isFinite(value) ? value : 0
}

const getSeedHash = (value: string) => {
  return Array.from(value).reduce((result, character) => {
    return (result * 31 + character.charCodeAt(0)) % 9973
  }, 7)
}

const getAudioVisual = (seedText: string) => {
  const seed = getSeedHash(seedText)
  const palette = previewPalettes[seed % previewPalettes.length]
  const bars = Array.from({ length: waveBarCount }, (_, index) => {
    const value = Math.sin((seed + index * 17) * 0.72)
    const normalizedValue = Math.abs(value)

    return 22 + Math.round(normalizedValue * 54)
  })

  return {
    bars,
    style: {
      "--audio-preview-bg-end": palette[1],
      "--audio-preview-bg-start": palette[0],
      "--audio-preview-color": palette[2],
    } as CSSProperties,
  }
}

export const AudioPreview = ({
  src,
  fingerprint,
  label = "音频",
  className,
  size = defaultPreviewFrameSize,
}: AudioPreviewProps) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const sliderMax = duration || 1
  const sliderValue = Math.min(currentTime, sliderMax)
  const audioVisual = getAudioVisual(fingerprint || src || label)

  const handleTogglePlay = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      return
    }

    try {
      if (duration > 0 && audio.currentTime >= duration) {
        audio.currentTime = 0
      }

      await audio.play()
    } catch {
      setIsPlaying(false)
    }
  }

  const syncDuration = () => {
    const audio = audioRef.current
    if (!audio) return

    setDuration(getFiniteMediaTime(audio.duration))
    setCurrentTime(getFiniteMediaTime(audio.currentTime))
  }

  const syncCurrentTime = () => {
    const audio = audioRef.current
    if (!audio) return

    setCurrentTime(getFiniteMediaTime(audio.currentTime))
  }

  const handleSeek = (value: number | [number, number]) => {
    const nextTime = Array.isArray(value) ? value[0] : value
    const audio = audioRef.current

    setCurrentTime(nextTime)

    if (!audio || duration <= 0) return

    audio.currentTime = nextTime
  }

  return (
    <Flex
      className={classNames(
        previewFrameClassName,
        getPreviewFrameSizeClassName(size),
        styles.preview,
        size === "small" && styles.previewSmall,
        className,
      )}
      align="center"
      justify="space-between"
      style={audioVisual.style}
      vertical
      gap={2}
    >
      <Flex
        className={styles.waveform}
        align="center"
        justify="space-between"
      >
        {audioVisual.bars.map((height, index) => (
          <span
            key={`${index}-${height}`}
            className={styles.waveBar}
            style={{ height: `${height}%` }}
          />
        ))}
      </Flex>
      <Text
        size="s"
        className={styles.label}
      >
        {label}
      </Text>
      <Button
        aria-label={`${isPlaying ? "暂停" : "播放"}${label}`}
        className={classNames(
          styles.playButton,
          size === "small" && styles.small,
        )}
        icon={isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
        shape="circle"
        size="small"
        type="text"
        onClick={handleTogglePlay}
        onKeyDown={stopPropagation}
      />
      <div
        className={styles.sliderWrap}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        onMouseDown={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <Slider
          className={styles.progressSlider}
          disabled={duration <= 0}
          max={sliderMax}
          min={0}
          step={0.1}
          tooltip={{ open: false }}
          value={sliderValue}
          onChange={handleSeek}
        />
      </div>
      <Text
        size={size === "small" ? 9 : 10}
        className={styles.timeText}
        type="secondary"
      >
        {formatTime(currentTime, 0)} /{" "}
        {duration ? formatTime(duration, 0) : "--:--"}
      </Text>
      <audio
        ref={audioRef}
        preload="metadata"
        src={src}
        onDurationChange={syncDuration}
        onEnded={() => {
          setIsPlaying(false)
          syncCurrentTime()
        }}
        onLoadedMetadata={syncDuration}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onTimeUpdate={syncCurrentTime}
      />
    </Flex>
  )
}
